import React from 'react';
import { Box, Typography } from '@mui/material';
import { FaPhoneAlt, FaUserTie, FaHeadset, FaRegBuilding, FaMapMarkerAlt, FaGlobeAsia } from 'react-icons/fa';
import { motion } from 'framer-motion';
import admissionDefault from '../../../../data/admission/admission.json';

const NAVY = '#0e2455';
const ORANGE = '#F6872A';

// Icon key → component (content stores a key string; the icon lives here).
const OFFICE_ICONS = { headset: FaHeadset, building: FaRegBuilding, pin: FaMapMarkerAlt, globe: FaGlobeAsia };

const AdmissionSidebar = ({ data }) => {
    const d = data || admissionDefault.sidebar;
    const regionalOffices = d.offices || [];

    const container = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.08, when: 'beforeChildren' } }
    };
    const item = {
        hidden: { opacity: 0, y: 14 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } }
    };

    return (
        <Box sx={{ position: 'sticky', top: '120px' }}>
            <Box
                component={motion.div}
                variants={container}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-40px' }}
                sx={{
                    borderRadius: '24px',
                    background: '#ffffff',
                    border: '1px solid #E8EDF5',
                    boxShadow: '0 20px 48px -32px rgba(14,36,85,0.3)',
                    overflow: 'hidden',
                }}
            >
                {/* Header */}
                <Box
                    component={motion.div}
                    variants={item}
                    sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        p: { xs: 3, md: 3.25 },
                        background: 'linear-gradient(135deg, #0e2455 0%, #1a3673 100%)',
                    }}
                >
                    <Box sx={{ position: 'absolute', top: -40, right: -40, width: 130, height: 130, borderRadius: '50%', background: 'rgba(246,135,42,0.18)', filter: 'blur(8px)' }} />
                    <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <Box sx={{ width: 42, height: 42, borderRadius: '12px', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FaUserTie color="#ffffff" size="18px" />
                        </Box>
                        <Typography sx={{ color: '#ffffff', fontWeight: 900, fontSize: '1.2rem', letterSpacing: '-0.3px' }}>
                            {d.heading}
                        </Typography>
                    </Box>
                    <Typography sx={{ position: 'relative', zIndex: 1, color: 'rgba(255,255,255,0.72)', fontSize: '0.85rem', fontWeight: 500, lineHeight: 1.5 }}>
                        {d.subtitle}
                    </Typography>
                </Box>

                {/* Offices */}
                <Box sx={{ p: { xs: 2, md: 2.25 }, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {regionalOffices.map((office, idx) => (
                        <Box
                            key={idx}
                            component={motion.div}
                            variants={item}
                            sx={{ p: 2, borderRadius: '16px', background: '#F8FAFC', border: '1px solid #EEF2F8' }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
                                <Box sx={{ flexShrink: 0, width: 32, height: 32, borderRadius: '9px', background: 'rgba(246,135,42,0.12)', color: ORANGE, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {React.createElement(OFFICE_ICONS[office.icon] || FaHeadset, { size: '15px' })}
                                </Box>
                                <Typography sx={{ color: NAVY, fontSize: '0.82rem', fontWeight: 800, lineHeight: 1.3 }}>
                                    {office.title}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {office.numbers.map((num, i) => (
                                    <Box
                                        key={i}
                                        component="a"
                                        href={`tel:${num.replace(/\s+/g, '')}`}
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.85,
                                            px: 1.25,
                                            py: 0.65,
                                            borderRadius: '10px',
                                            background: '#ffffff',
                                            border: '1px solid #E8EDF5',
                                            color: NAVY,
                                            fontWeight: 800,
                                            fontSize: '0.85rem',
                                            textDecoration: 'none',
                                            transition: 'all 0.2s ease',
                                            '& svg': { color: ORANGE, transition: 'color 0.2s ease' },
                                            '&:hover': {
                                                background: NAVY,
                                                borderColor: NAVY,
                                                color: '#ffffff',
                                                transform: 'translateY(-1px)',
                                                '& svg': { color: '#ffffff' },
                                            },
                                        }}
                                    >
                                        <FaPhoneAlt size="11px" />
                                        {num}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

export default AdmissionSidebar;
